import { unitPricingToTotalNet } from './unit-pricing-to-total-net.function';

/**
 * Calculates a pricing item’s commission net amount.
 *
 * @param pricingItem The item to price:
 * 	- `units`             – how many units
 * 	- `unitPrice`         – price per unit (net or gross)
 * 	- `unitTaxRate`       – tax rate as a percentage (e.g. 19 for 19%)
 * 	- `isTaxIncluded`     – whether `unitPrice` already includes tax (gross). Defaults to false.
 * 	- `unitCommissionRate`– commission rate as a percentage (e.g. 10 for 10%)
 * 	- `isCommissionEligible` – whether the item is eligible for commission
 * @returns The commission net amount for all units.
 * @example
 * ```ts
 * const pricingItem = {
 *   units: 4,
 *   unitPrice: 100,
 *   unitTaxRate: 19,
 *   isTaxIncluded: true,
 *   unitCommissionRate: 10,
 *   isCommissionEligible: true,
 * };
 * const commissionNet = unitPricingToCommissionNet(pricingItem);
 * console.log(commissionNet); // Output: 33.61344537815126
 * ```
 */
export function unitPricingToCommissionNet(pricingItem: {
	units?: number;
	unitPrice?: number;
	unitTaxRate?: number;
	isTaxIncluded?: boolean;
	unitCommissionRate?: number;
	isCommissionEligible?: boolean;
}): number {
	if (!pricingItem.isCommissionEligible) {
		return 0;
	}

	const units =
		pricingItem.units == null ||
		isNaN(pricingItem.units) ||
		pricingItem.units < 0
			? 0
			: pricingItem.units;
	const totalNet = unitPricingToTotalNet(pricingItem) * units;
	const commission = totalNet * ((pricingItem.unitCommissionRate ?? 0) / 100);

	return commission;
}
